import { Fragment, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowForward } from '@mui/icons-material';
import { CircularProgress, Pagination, Tooltip } from '@mui/material';
import { format } from 'date-fns';

import { getStatusMovements } from 'api/rest/analytics';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { caseStatusOptions } from 'data/constants/caseStatus';
import type { StatusMovementRow } from 'hooks/analytics';
import { AnalyticsColors } from '../../components/analyticsUi';
import CollapsibleSection from '../../components/CollapsibleSection';
import useAnalyticsData from '../../hooks/useAnalyticsData';

const STATUS_META: Record<string, { label: string; color: string }> = caseStatusOptions.reduce(
    (acc, option) => ({ ...acc, [option.value]: { label: option.label, color: option.color } }),
    {}
);

const withAlpha = (color: string, alpha: number) =>
    color.replace(/rgba?\(([^)]+)\)/, (_match, inner: string) => {
        const [r, g, b] = inner.split(',').map((part) => part.trim());
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    });

const PAGE_SIZE = 10;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th<{ align?: string; sortable?: boolean; active?: boolean }>`
    text-align: ${({ align }) => align || 'left'};
    font-size: 11px;
    font-weight: 600;
    color: ${({ active }) => (active ? AnalyticsColors.primary : AnalyticsColors.textTertiary)};
    text-transform: uppercase;
    letter-spacing: 0.6px;
    padding: 0.6rem 0.75rem;
    border-bottom: 2px solid ${AnalyticsColors.borderLight};
    cursor: ${({ sortable }) => (sortable ? 'pointer' : 'default')};
    user-select: none;
    transition: color 0.15s ease;

    &:hover {
        color: ${({ sortable }) =>
            sortable ? AnalyticsColors.primary : AnalyticsColors.textTertiary};
    }
`;

const Tr = styled.tr`
    transition: background 0.1s;
    &:hover {
        background: #f9fafb;
    }
`;

const Td = styled.td<{ align?: string }>`
    font-size: 13.5px;
    color: ${AnalyticsColors.textPrimary};
    padding: 0.75rem;
    border-bottom: 1px solid ${AnalyticsColors.borderLight};
    text-align: ${({ align }) => align || 'left'};
    vertical-align: middle;
`;

const SortHeader = styled.span<{ align?: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    justify-content: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
    width: 100%;
`;

const SortIndicator = styled.span<{ active: boolean }>`
    font-size: 10px;
    line-height: 1;
    opacity: ${({ active }) => (active ? 1 : 0.25)};
    color: ${({ active }) => (active ? AnalyticsColors.primary : AnalyticsColors.textTertiary)};
`;

const StateMessage = styled.div`
    padding: 2rem 0;
    text-align: center;
    color: ${AnalyticsColors.textSecondary};
    font-size: 13px;
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 2rem 0;
`;

const ClientName = styled.span`
    font-weight: 600;
`;

const ProgramName = styled.span`
    color: ${AnalyticsColors.textSecondary};
    font-size: 13px;
`;

const JourneyTrack = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    row-gap: 8px;
`;

const StatusPill = styled.span<{ color: string }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    background: ${({ color }) => withAlpha(color, 0.1)};
    color: ${({ color }) => color};
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: default;
`;

const StatusDot = styled.span<{ color: string }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ color }) => color};
    flex-shrink: 0;
`;

const ArrowWrap = styled.span`
    display: inline-flex;
    color: ${AnalyticsColors.textTertiary};
    flex-shrink: 0;
`;

const ChangeCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    padding: 2px 8px;
    border-radius: 999px;
    background: ${AnalyticsColors.primaryLight};
    color: ${AnalyticsColors.primary};
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
`;

const LastChangeDate = styled.span`
    color: ${AnalyticsColors.textSecondary};
    font-size: 13px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`;

const FilterGroup = styled.div`
    margin-bottom: 12px;
`;

const FilterGroupLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${AnalyticsColors.textTertiary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
`;

const FilterBar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    row-gap: 8px;
`;

const FilterChip = styled.button<{ color: string; selected: boolean; strike?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid ${({ color, selected }) => (selected ? color : AnalyticsColors.borderLight)};
    background: ${({ color, selected }) =>
        selected ? withAlpha(color, 0.12) : AnalyticsColors.cardBg};
    color: ${({ color, selected }) => (selected ? color : AnalyticsColors.textSecondary)};
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    text-decoration: ${({ strike, selected }) => (strike && selected ? 'line-through' : 'none')};
    transition:
        border-color 0.15s ease,
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        border-color: ${({ color }) => color};
        color: ${({ color }) => color};
    }

    &:focus-visible {
        outline: 2px solid ${AnalyticsColors.primary};
        outline-offset: 1px;
    }
`;

const ClearFiltersButton = styled.button`
    background: transparent;
    border: none;
    color: ${AnalyticsColors.primary};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;

    &:hover {
        text-decoration: underline;
    }
`;

const FooterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
`;

const ShowingText = styled.span`
    font-size: 12.5px;
    color: ${AnalyticsColors.textSecondary};
    font-variant-numeric: tabular-nums;
`;

const TableScroll = styled.div`
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
`;

type SortKey = 'changes' | 'lastChange';
type SortDirection = 'asc' | 'desc';

const changeCount = (row: StatusMovementRow) => row.journey.length - 1;

const lastChangeDate = (row: StatusMovementRow): string => {
    for (let i = row.journey.length - 1; i >= 0; i -= 1) {
        const { date } = row.journey[i];
        if (date) return date;
    }
    return '';
};

const formatDate = (date?: string | null) =>
    date ? format(new Date(`${date}T00:00:00`), 'MMM d, yyyy') : 'N/A';

interface StatusMovementsProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const StatusMovements = ({ from, to, serviceProviderId }: StatusMovementsProps) => {
    const [sortKey, setSortKey] = useState<SortKey>('lastChange');
    const [direction, setDirection] = useState<SortDirection>('desc');
    const [includeStatuses, setIncludeStatuses] = useState<ClientApplicationStatus[]>([]);
    const [excludeStatuses, setExcludeStatuses] = useState<ClientApplicationStatus[]>([]);
    const [page, setPage] = useState(0);

    const { data, loading, error } = useAnalyticsData(
        () =>
            getStatusMovements({
                from,
                to,
                serviceProviderId,
                includeStatuses,
                excludeStatuses,
                page: page + 1,
                pageSize: PAGE_SIZE,
            }),
        [from, to, serviceProviderId, includeStatuses, excludeStatuses, page]
    );

    const totalCases = data?.pagination.total ?? 0;
    const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
    const safePage = Math.min(page, totalPages - 1);

    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    // A status can only be in one of the two filters; selecting it in one removes it from the other.
    const toggleInclude = (status: ClientApplicationStatus) => {
        setIncludeStatuses((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
        setExcludeStatuses((prev) => prev.filter((s) => s !== status));
        setPage(0);
    };

    const toggleExclude = (status: ClientApplicationStatus) => {
        setExcludeStatuses((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
        setIncludeStatuses((prev) => prev.filter((s) => s !== status));
        setPage(0);
    };

    const clearStatuses = () => {
        setIncludeStatuses([]);
        setExcludeStatuses([]);
        setPage(0);
    };

    const toggleSort = (key: SortKey) => {
        if (key === sortKey) {
            setDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setDirection('desc');
        }
        setPage(0);
    };

    const rows = useMemo(() => {
        const rows = [...(data?.statusMovements ?? [])];
        rows.sort((a, b) => {
            const diff =
                sortKey === 'changes'
                    ? changeCount(a) - changeCount(b)
                    : lastChangeDate(a).localeCompare(lastChangeDate(b));
            return direction === 'desc' ? -diff : diff;
        });
        return rows;
    }, [data?.statusMovements, sortKey, direction]);

    const rangeStart = totalCases === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeEnd = Math.min((safePage + 1) * PAGE_SIZE, totalCases);

    const renderSortableTh = (key: SortKey, label: string, align?: 'left' | 'center' | 'right') => {
        const active = sortKey === key;
        return (
            <Th
                align={align}
                sortable
                active={active}
                onClick={() => toggleSort(key)}
                aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
            >
                <SortHeader align={align}>
                    {label}
                    <SortIndicator active={active}>
                        {active && direction === 'asc' ? '▲' : '▼'}
                    </SortIndicator>
                </SortHeader>
            </Th>
        );
    };

    return (
        <CollapsibleSection
            id="status-movements"
            title="Status Movements"
            tooltip="Clients whose case status changed during the selected period, with the full sequence of statuses they moved through. Use the first filter row to only show cases that passed through the selected statuses, and the second to hide cases that did. Hover a status for the date of the change."
        >
            <FilterGroup>
                <FilterGroupLabel>Has had status in period</FilterGroupLabel>
                <FilterBar role="group" aria-label="Include cases that had these statuses">
                    {caseStatusOptions.map((option) => {
                        const status = option.value as ClientApplicationStatus;
                        const selected = includeStatuses.includes(status);
                        return (
                            <FilterChip
                                key={option.value}
                                type="button"
                                color={option.color}
                                selected={selected}
                                aria-pressed={selected}
                                onClick={() => toggleInclude(status)}
                            >
                                <StatusDot color={option.color} />
                                {option.label}
                            </FilterChip>
                        );
                    })}
                </FilterBar>
            </FilterGroup>

            <FilterGroup style={{ marginBottom: '16px' }}>
                <FilterGroupLabel>Has not had status in period</FilterGroupLabel>
                <FilterBar role="group" aria-label="Exclude cases that had these statuses">
                    {caseStatusOptions.map((option) => {
                        const status = option.value as ClientApplicationStatus;
                        const selected = excludeStatuses.includes(status);
                        return (
                            <FilterChip
                                key={option.value}
                                type="button"
                                color={option.color}
                                selected={selected}
                                strike
                                aria-pressed={selected}
                                onClick={() => toggleExclude(status)}
                            >
                                <StatusDot color={option.color} />
                                {option.label}
                            </FilterChip>
                        );
                    })}
                    {(includeStatuses.length > 0 || excludeStatuses.length > 0) && (
                        <ClearFiltersButton type="button" onClick={clearStatuses}>
                            Clear filters
                        </ClearFiltersButton>
                    )}
                </FilterBar>
            </FilterGroup>

            {loading ? (
                <LoadingWrap>
                    <CircularProgress size={28} />
                </LoadingWrap>
            ) : error ? (
                <StateMessage>{error}</StateMessage>
            ) : totalCases === 0 ? (
                <StateMessage>
                    No cases match the selected status filters for this period.
                </StateMessage>
            ) : (
                <>
                    <TableScroll>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Client</Th>
                                    <Th>Program / Service</Th>
                                    <Th>Status Journey</Th>
                                    {renderSortableTh('changes', 'Changes', 'center')}
                                    {renderSortableTh('lastChange', 'Last Change', 'right')}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <Tr key={row.id}>
                                        <Td>
                                            <ClientName>{row.clientName}</ClientName>
                                        </Td>
                                        <Td>
                                            <ProgramName>{row.programName}</ProgramName>
                                        </Td>
                                        <Td>
                                            <JourneyTrack>
                                                {row.journey.map((step, index) => {
                                                    const meta = STATUS_META[step.status];
                                                    if (!meta) return null;
                                                    const tooltip = step.date
                                                        ? `Changed on ${formatDate(step.date)}`
                                                        : 'Status at start of period';
                                                    return (
                                                        <Fragment key={`${step.status}-${index}`}>
                                                            {index > 0 && (
                                                                <ArrowWrap aria-hidden="true">
                                                                    <ArrowForward
                                                                        sx={{ fontSize: 13 }}
                                                                    />
                                                                </ArrowWrap>
                                                            )}
                                                            <Tooltip title={tooltip} arrow>
                                                                <StatusPill color={meta.color}>
                                                                    <StatusDot color={meta.color} />
                                                                    {meta.label}
                                                                </StatusPill>
                                                            </Tooltip>
                                                        </Fragment>
                                                    );
                                                })}
                                            </JourneyTrack>
                                        </Td>
                                        <Td align="center">
                                            <ChangeCount>{changeCount(row)}</ChangeCount>
                                        </Td>
                                        <Td align="right">
                                            <LastChangeDate>
                                                {formatDate(lastChangeDate(row))}
                                            </LastChangeDate>
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableScroll>

                    <FooterRow>
                        <ShowingText>
                            Showing {rangeStart}–{rangeEnd} of {totalCases} case
                            {totalCases === 1 ? '' : 's'}
                        </ShowingText>
                        {totalPages > 1 && (
                            <Pagination
                                color="primary"
                                size="small"
                                count={totalPages}
                                page={safePage + 1}
                                siblingCount={1}
                                boundaryCount={1}
                                aria-label="Table pagination"
                                onChange={(_event, value) => setPage(value - 1)}
                                sx={{
                                    maxWidth: '100%',
                                    '& .MuiPagination-ul': {
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-end',
                                    },
                                }}
                            />
                        )}
                    </FooterRow>
                </>
            )}
        </CollapsibleSection>
    );
};

export default StatusMovements;
